import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PlatformService } from './platform.service';

export interface AuthResponse {
  accessToken: string;
  expiresIn: number;
  address: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private platformService = inject(PlatformService);

  /**
   * Get the authentication message from the backend
   */
  async getAuthMessage(): Promise<string> {
    if (this.platformService.isServer) {
      return '';
    }

    try {
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      const response = await firstValueFrom(
        this.http.post<{ message: string }>(`${baseUrl}/api/auth/message`, {})
      );
      
      return response.message;
    } catch (error) {
      console.error('[AuthService] Error fetching auth message:', error);
      throw error;
    }
  }

  /**
   * Exchange Web3 signature for JWT token
   */
  async signin(address: string, signature: string): Promise<AuthResponse> {
    if (this.platformService.isServer) {
      throw new Error('Cannot sign in on server');
    }

    try {
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      const response = await firstValueFrom(
        this.http.post<AuthResponse>(`${baseUrl}/api/auth/signin`, {
          address: address.toLowerCase(),
          signature
        })
      );
      
      return response;
    } catch (error) {
      console.error('[AuthService] Error during signin:', error);
      throw error;
    }
  }

  /**
   * Refresh JWT token
   */
  async refreshToken(token: string): Promise<AuthResponse> {
    if (this.platformService.isServer) {
      throw new Error('Cannot refresh token on server');
    }

    try {
      const graphUri = environment.alternativeListingsGraphURI || '';
      const baseUrl = graphUri.replace(/\/graphql$/, '');
      
      if (!baseUrl) {
        throw new Error('alternativeListingsGraphURI is not configured');
      }

      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${baseUrl}/api/auth/refresh`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )
      );
      
      return response;
    } catch (error) {
      console.error('[AuthService] Error refreshing token:', error);
      throw error;
    }
  }
}


